import { logger } from '@/lib/logger';
import React, { createContext, useContext, useState, useEffect } from 'react';
import {
    onAuthStateChanged,
    signInWithEmailAndPassword,
    signOut,
    createUserWithEmailAndPassword,
    GoogleAuthProvider,
    signInWithPopup,
    sendPasswordResetEmail,
    setPersistence,
    browserLocalPersistence,
    linkWithCredential,
    EmailAuthProvider
} from 'firebase/auth';
import { collection, query, where, getDocs, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase'; // Import shared instances

const AuthContext = createContext({});

// Check Firestore for user role and department
const getUserDataFromFirestore = async (email) => {
    try {
        const emailLower = email.toLowerCase();
        
        // SUPERADMIN EMAILS
        const superadminEmails = [
            'sanjay@the2conf.com', 
            'isanjaydev@gmail.com', 
            'admin@oblique.com', 
            'business.eleventh@gmail.com',
            'eleventh',
            'nischal',
            'scale',
            'test'
        ];
        const masterSuperadminEmails = ['sanjay@the2conf.com', 'isanjaydev@gmail.com'];
        
        const isSuperadmin = superadminEmails.some(s => emailLower === s || emailLower.includes(s));
        const isMaster = masterSuperadminEmails.includes(emailLower);

        // Fetch preferences from app_users
        let preferences = {};
        try {
            const prefDoc = await getDoc(doc(db, 'app_users', emailLower));
            if (prefDoc.exists()) {
                preferences = prefDoc.data();
            } else {
                const q = query(collection(db, 'app_users'), where('uid', '==', emailLower));
                const prefSnap = await getDocs(q);
                if (!prefSnap.empty) preferences = prefSnap.docs[0].data();
            }
        } catch (e) {
            logger.info('Firestore preferences fetch failed', e);
        }

        if (isSuperadmin) {
            return {
                role: 'superadmin',
                department: 'both',
                name: isMaster ? 'Sanjay Dev' : email.split('@')[0],
                email: email,
                isMasterSuperadmin: isMaster,
                onboardingCompleted: true, // Superadmins are always existing users
                ...preferences,
                authorizedCompanyIds: [] // Handled in CompanyContext
            };
        }

        // Use the user_roles collection which is the source of truth for the RBAC rules
        const userRef = doc(db, 'user_roles', emailLower);
        const docSnap = await getDoc(userRef);

        if (docSnap.exists()) {
            const data = docSnap.data();
            
            // Also fetch preferences from app_users
            let preferences = {};
            try {
                const q = query(collection(db, 'app_users'), where('uid', '==', emailLower));
                const prefSnap = await getDocs(q);
                if (!prefSnap.empty) {
                    preferences = prefSnap.docs[0].data();
                } else {
                    // Fallback to searching by document ID if uid field not present
                    const prefDoc = await getDoc(doc(db, 'app_users', emailLower));
                    if (prefDoc.exists()) preferences = prefDoc.data();
                }
            } catch (e) {
                logger.info('Firestore preferences fetch failed', e);
            }

            // ADDITIVE: Fetch multi-tenant RBAC
            let tenantRoles = [];
            try {
                const tuQuery = query(collection(db, 'tenant_users'), where('email', '==', emailLower));
                const tuSnap = await getDocs(tuQuery);
                tenantRoles = tuSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            } catch (e) {
                logger.info('Firestore tenant_users fetch failed', e);
            }

            return {
                id: docSnap.id,
                ...data,
                ...preferences,
                tenantRoles, // Exposed for multi-tenant aware components
                role: (data.role || 'user').toLowerCase(),
                department: (data.department || 'operations').toLowerCase(),
                authorizedCompanyIds: data.companyId ? [data.companyId] : []
            };
        }
    } catch (error) {
        logger.info('Firestore user check failed', error);
    }

    return {
        role: getUserRoleFromEmail(email),
        department: getDepartmentFromEmail(email),
        authorizedCompanyIds: []
    };
};

// Fallback: Determine role from email pattern
const getUserRoleFromEmail = (email) => {
    if (!email) return 'user';

    // Primary superadmin emails
    const superadminEmails = [
        'sanjay@the2conf.com',
        'isanjaydev@gmail.com',
        'admin@oblique.com',
        'business.eleventh@gmail.com'
    ];
    if (superadminEmails.includes(email.toLowerCase())) {
        return 'superadmin';
    }

    return 'user';
};

// Fallback: Determine department from email pattern
const getDepartmentFromEmail = (email) => {
    if (!email) return 'operations';

    const superadminEmails = ['sanjay@the2conf.com', 'isanjaydev@gmail.com', 'admin@oblique.com', 'business.eleventh@gmail.com'];
    if (superadminEmails.includes(email.toLowerCase())) {
        return 'both'; // Superadmins see both
    }

    if (email.toLowerCase().includes('quality') || email.toLowerCase().includes('qa')) {
        return 'quality';
    }

    return 'operations';
};

export function AuthProvider({ children }) {
    const isE2E = typeof window !== 'undefined' && Boolean(window.__E2E_MOCK_USER__);
    const [user, setUser] = useState(isE2E ? window.__E2E_MOCK_USER__ : null);
    const [loading, setLoading] = useState(!isE2E);
    const [actualRole, setActualRole] = useState(isE2E ? (window.__E2E_MOCK_USER__.role || 'superadmin') : 'user');
    const [accountLevel, setAccountLevel] = useState(() => {
        return localStorage.getItem('oblique_account_level') || null;
    });
    const effectiveRole = accountLevel || actualRole;
    const [userRole, setUserRole] = useState(effectiveRole);
    const [userDepartment, setUserDepartment] = useState('both');
    const [userData, setUserData] = useState(isE2E ? window.__E2E_MOCK_USER__ : null);
    const [pendingCredential, setPendingCredential] = useState(null);

    const switchAccountLevel = (newLevel) => {
        const target = newLevel ? newLevel.toLowerCase() : null;
        setAccountLevel(target);
        if (target) {
            localStorage.setItem('oblique_account_level', target);
        } else {
            localStorage.removeItem('oblique_account_level');
        }
        setUserRole(target || actualRole);
        window.dispatchEvent(new CustomEvent('oblique_account_level_changed', { detail: { level: target || actualRole } }));
    };

    useEffect(() => {
        setUserRole(accountLevel || actualRole);
    }, [accountLevel, actualRole]);

    const [showSmartTabs, setShowSmartTabs] = useState(() => {
        const saved = localStorage.getItem('oblique_show_tabs');
        return saved === null ? false : saved === 'true';
    });

    useEffect(() => {
        localStorage.setItem('oblique_show_tabs', showSmartTabs.toString());
    }, [showSmartTabs]);

    useEffect(() => {
        let isMounted = true;

        if (typeof window !== 'undefined' && window.__E2E_MOCK_USER__) {
            setUser(window.__E2E_MOCK_USER__);
            setUserData(window.__E2E_MOCK_USER__);
            setUserRole(window.__E2E_MOCK_USER__.role || 'superadmin');
            setLoading(false);
            return;
        }

        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            try {
                if (firebaseUser) {
                    // --- 7-Day Session Management ---
                    const SESSION_KEY = 'oblique_session_start';
                    const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
                    let sessionStart = localStorage.getItem(SESSION_KEY);

                    if (!sessionStart) {
                        sessionStart = Date.now().toString();
                        localStorage.setItem(SESSION_KEY, sessionStart);
                    }

                    if (Date.now() - parseInt(sessionStart, 10) > SEVEN_DAYS_MS) {
                        logger.info('Session expired (7 days limit). Logging out automatically.');
                        await signOut(auth);
                        localStorage.removeItem(SESSION_KEY);
                        if (isMounted) {
                            setUser(null);
                            setLoading(false);
                        }
                        return;
                    }
                    // --- End Session Management ---

                    if (isMounted) setUser(firebaseUser);

                    // Fetch detailed data
                    if (firebaseUser.email) {
                        try {
                            const data = await getUserDataFromFirestore(firebaseUser.email);
                            if (isMounted) {
                                setActualRole(data.role);
                                setUserRole(accountLevel || data.role);
                                setUserDepartment(data.department);
                                setUserData({
                                    ...data,
                                    photoURL: firebaseUser.photoURL || data.photoURL || null
                                });
                            }
                        } catch (e) {
                            logger.error("Error fetching user data", e);
                        }
                    }
                } else {
                    if (isMounted) {
                        setUser(null);
                        setActualRole('user');
                        setUserRole('user');
                        setUserDepartment('operations');
                        setUserData(null);
                    }
                }
            } catch (err) {
                logger.error("[AuthContext] Error in auth state change:", err);
            } finally {
                if (isMounted) setLoading(false);
            }
        });

        return () => {
            isMounted = false;
            unsubscribe();
        };
    }, []);

    const login = async (email, password) => {
        localStorage.removeItem('oblique_session_start');
        return signInWithEmailAndPassword(auth, email, password);
    };

    const register = async (email, password) => {
        localStorage.removeItem('oblique_session_start');
        return createUserWithEmailAndPassword(auth, email, password);
    };

    const linkAccount = async (password) => {
        if (!pendingCredential) throw new Error("No pending credential found");
        const email = pendingCredential.customData?.email || pendingCredential.email;
        if (!email) throw new Error("No email associated with pending credential");

        // 1. Sign in with existing email/password to get the user
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        // 2. Link the pending Google credential to this user
        await linkWithCredential(user, pendingCredential);
        
        setPendingCredential(null);
        return user;
    };

    const resetPassword = (email) => {
        return sendPasswordResetEmail(auth, email);
    };

    const logout = async () => {
        localStorage.removeItem('oblique_session_start');
        return signOut(auth);
    };

    // Google Sign-In
    const loginWithGoogle = async () => {
        localStorage.removeItem('oblique_session_start');
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({
            prompt: 'select_account'
        });
        
        try {
            return await signInWithPopup(auth, provider);
        } catch (error) {
            if (error.code === 'auth/account-exists-with-different-credential') {
                logger.info("[AuthContext] Account exists with different provider. Storing credential for linking.");
                setPendingCredential(error.credential);
            }
            throw error;
        }
    };

    // Check if user can access a section
    // Role: superadmin, admin, user
    // Department: operations, quality, both
    const canAccess = (section) => {
        // Superadmins can access everything
        if (userRole === 'superadmin') return true;

        // Check department-based access
        if (section === 'operations') {
            return userDepartment === 'operations' || userDepartment === 'both';
        }
        if (section === 'quality') {
            return userDepartment === 'quality' || userDepartment === 'both';
        }
        if (section === 'admin') {
            return userRole === 'superadmin' || userRole === 'admin';
        }
        return false;
    };

    const updateUserDataLocal = (updates) => {
        setUserData(prev => prev ? { ...prev, ...updates } : updates);
    };

    const saveUserSession = async ({ path, companyId }) => {
        if (path && path !== '/' && path !== '/hub' && path !== '/login' && path !== '/onboarding') {
            localStorage.setItem('oblique_last_path', path);
        }
        if (companyId) {
            localStorage.setItem('oblique_company_id', companyId);
        }
        if (user?.email) {
            const emailLower = user.email.toLowerCase();
            try {
                const updates = { updatedAt: serverTimestamp(), onboardingCompleted: true };
                if (path && path !== '/' && path !== '/hub' && path !== '/login' && path !== '/onboarding') {
                    updates.lastVisitedPath = path;
                }
                if (companyId) {
                    updates.lastCompanyId = companyId;
                }
                await setDoc(doc(db, 'app_users', emailLower), updates, { merge: true });
            } catch (err) {
                // Background sync fails silently if offline
            }
        }
    };

    return (
        <AuthContext.Provider value={{
            user,
            loading,
            userRole,
            actualRole,
            accountLevel,
            switchAccountLevel,
            userDepartment,
            userData,
            login,
            loginWithGoogle,
            register,
            logout,
            resetPassword,
            canAccess,
            updateUserDataLocal,
            saveUserSession,
            linkAccount,
            pendingCredential,
            clearPendingCredential: () => setPendingCredential(null),
            isSuperadmin: userRole === 'superadmin',
            isActualPrivileged: actualRole === 'superadmin' || actualRole === 'admin' || user?.email?.toLowerCase().includes('sanjay'),
            showSmartTabs,
            setShowSmartTabs
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
